import React, { useEffect } from 'react';
import { X } from 'lucide-react';
import { VideoPlayer } from './VideoPlayer';
import { VideoPlayerModalProps } from './types';
import { useIsMobile } from '@/hooks/useIsMobile';

const mobileVideoStyles = `
  .video-js video {
    object-fit: cover !important;
  }
`;

export function VideoPlayerModal({
  isOpen,
  videoUrl,
  onClose,
  playing = true,
  controls = true,
  loop = false,
  muted = false,
  isPhoneFrameMode = false,
}: VideoPlayerModalProps): React.ReactElement | null {
  const isMobile = useIsMobile();

  // Set CSS custom property for mobile viewport height
  useEffect(() => {
    const setVH = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };

    setVH();
    window.addEventListener('resize', setVH);
    return () => window.removeEventListener('resize', setVH);
  }, []);

  useEffect(() => {
    if (isPhoneFrameMode) return;
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen, isPhoneFrameMode]);

  if (!isOpen) return null;

  if (isPhoneFrameMode) {
    return (
      <div className="video-modal" style={styles.modalPhoneFrame} onClick={onClose}>
        {isMobile && <style>{mobileVideoStyles}</style>}
        <button style={{ ...styles.closeButton, ...styles.closeButtonPhoneFrame }} onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}>
          <X size={50} />
        </button>
        <div className="video-player-wrapper" style={styles.wrapperPhoneFrame}>
          <VideoPlayer
            url={videoUrl}
            playing={playing}
            controls={controls}
            loop={loop}
            muted={muted}
          />
        </div>
      </div>
    );
  }

  if (isMobile) {
    return (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.95)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
        }}
        onClick={onClose}
      >
        <style>{mobileVideoStyles}</style>
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '30px',
            left: '20px',
            background: 'rgba(0, 0, 0, 0.8)',
            border: 'none',
            borderRadius: '50%',
            width: '40px',
            height: '40px',
            cursor: 'pointer',
            color: 'white',
            zIndex: 10000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <X size={50} />
        </button>
        <div
          style={{
            width: '100vw',
            height: 'calc(var(--vh, 1vh) * 100)',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <VideoPlayer
            url={videoUrl}
            playing={playing}
            controls={controls}
            loop={loop}
            muted={muted}
          />
        </div>
      </div>
    );
  }

  // Desktop view - contained video with default object-fit
  return (
    <div className="video-modal" onClick={onClose}>
      <button
        className="modal-close-button"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
      >
        <X size={28} />
      </button>
      <div
        className="video-player-wrapper"
        onClick={(e) => e.stopPropagation()}
        style={{ width: '100vw', height: '100vh' }}
      >
        <VideoPlayer
          url={videoUrl}
          playing={playing}
          controls={controls}
          loop={loop}
        />
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  modalPhoneFrame: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '100%',
    height: '100%',
    borderRadius: '45px',
    overflow: 'hidden',
  },
  wrapperPhoneFrame: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
  },
  closeButton: {
    position: 'fixed',
    top: '30px',
    left: '20px',
    background: 'rgba(0, 0, 0, 0.8)',
    border: 'none',
    borderRadius: '50%',
    width: '40px',
    height: '40px',
    color: 'white',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
  },
  closeButtonPhoneFrame: {
    position: 'absolute',
    top: '50px',
    right: '20px',
  },
};
